import { useTranslation } from "react-i18next";
import { Icon, type IconName } from "@nexus/ui";
import type { BigQueryUsageMonitoring } from "@/features/bigquery-usage/api/bigquery-usage.api";
import { monitoringQueryCount, type BigQueryZeroState } from "../lib/zero-state";

export interface BigQueryZeroStateNoticeProps {
  state: BigQueryZeroState;
  /** Only for the broken state's count, which is Google's rather than Nexus's. */
  monitoring: BigQueryUsageMonitoring | undefined;
}

const TITLE_CLASS = "text-xs font-medium text-foreground";
const NOTE_CLASS = "text-[11px] text-muted-foreground";

interface StateSignal {
  icon: IconName;
  isFault: boolean;
}

/**
 * A quiet window is the ordinary case, so it takes the same neutral mark the
 * environment-level state does. Only the broken one is marked as a fault, and
 * every one of the three carries its own words as well as its own glyph.
 */
const ZERO_STATE_SIGNALS: Record<BigQueryZeroState, StateSignal> = {
  "not-recording": { icon: "info", isFault: false },
  "no-usage-in-window": { icon: "info", isFault: false },
  "recording-broken": { icon: "alert-circle", isFault: true },
  // A glyph that points at the controls the admin can reach, rather than the
  // neutral one the environment-level states carry.
  "no-match-for-narrowing": { icon: "filter", isFault: false },
};

/**
 * R19's three zero states, each in its own words, at the top of the tab.
 *
 * It leads the page rather than sitting with the reconciliation panel because
 * every surface below it — the KPI strip, the chart, and the three breakdowns —
 * decides what to say from a row count, and a row count cannot tell an
 * environment that records nothing from an hour in which nothing ran. Read in
 * that order the tab reports zero usage four times before naming the reason;
 * read in this one the reason is the first thing on screen and the zeros
 * beneath it are its consequence.
 *
 * The quiet window is written to read as unremarkable, because it is the state
 * a healthy production environment lands in most often and reading it as a
 * warning would send an operator looking for a fault that is not there.
 */
export function BigQueryZeroStateNotice({ state, monitoring }: BigQueryZeroStateNoticeProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const signal = ZERO_STATE_SIGNALS[state];

  const copy: Record<BigQueryZeroState, { title: string; note: string }> = {
    "not-recording": {
      title: t(
        "admin.bigQueryUsage.zeroState.notRecordingTitle",
        "No telemetry is recorded in this environment",
      ),
      note: t(
        "admin.bigQueryUsage.zeroState.notRecordingNote",
        "This deployment does not run the BigQuery analytics backend, so no query telemetry is being written here. Anything on this tab was recorded earlier, and a zero is the absence of recording rather than the absence of usage.",
      ),
    },
    "no-usage-in-window": {
      title: t("admin.bigQueryUsage.zeroState.noUsageTitle", "No BigQuery usage in this window"),
      note: t(
        "admin.bigQueryUsage.zeroState.noUsageNote",
        "Telemetry is recording normally and this window was simply quiet.",
      ),
    },
    "recording-broken": {
      title: t("admin.bigQueryUsage.zeroState.brokenTitle", "Recording appears to be broken"),
      note: t(
        "admin.bigQueryUsage.zeroState.brokenNote",
        "Nexus recorded no queries here while Cloud Monitoring counted {{queries}} on the same project, so telemetry that should have been written was not.",
        // This state is only ever resolved from a count Monitoring returned, so
        // the fallback is unreachable rather than a figure to trust.
        { queries: (monitoringQueryCount(monitoring) ?? 0).toLocaleString(locale) },
      ),
    },
    "no-match-for-narrowing": {
      title: t(
        "admin.bigQueryUsage.zeroState.noMatchTitle",
        "No queries match this search or these filters",
      ),
      // Names the three ways back out. A dead end here is worse than in the other
      // three states, because this is the one the admin can undo themselves.
      note: t(
        "admin.bigQueryUsage.zeroState.noMatchNote",
        "Telemetry is recording normally. Widen the window, clear the search, or remove a filter to see more.",
      ),
    },
  };

  const { title, note } = copy[state];

  return (
    <section
      aria-label={t("admin.bigQueryUsage.zeroState.region", "Why this window is empty")}
      className="flex shrink-0 items-start gap-2 rounded-md border border-border bg-card px-3 py-2"
    >
      {/* The glyph never carries the state on its own: the title beside it says
          which of the three this is, in words. */}
      <Icon
        name={signal.icon}
        size="xs"
        color={signal.isFault ? "destructive" : "muted"}
        className="mt-0.5"
        aria-hidden
      />
      <div className="flex flex-col gap-0.5">
        <p className={TITLE_CLASS}>{title}</p>
        <p className={NOTE_CLASS}>{note}</p>
      </div>
    </section>
  );
}
